import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SeasonPassService } from 'src/app/services/season-pass.service';
import { SeasonPass } from 'src/app/interfaces/abonado';
import { AppSettings } from 'src/app/app-settings';
import { seasonPassSeatLabel } from 'src/app/helpers/season-pass-seat';

/**
 * Carnet digital del abono: página con el QR (el `hash` del abono) y sus datos
 * para acceder a los eventos. Solo tiene sentido para abonos activos, así que
 * si el abono no existe o no está pagado se vuelve a "Mis abonos".
 */
@Component({
  selector: 'app-abono-card',
  templateUrl: './abono-card.component.html',
  styleUrls: ['./abono-card.component.scss'],
})
export class AbonoCardComponent implements OnInit {

  seasonPass: SeasonPass | null = null;
  loaded = false;
  loadError = false;

  // Wallet solo se ofrece donde está encendido por entorno (hoy dev y local):
  // en pre y producción no se pinta el botón hasta que se desarrolle Apple
  // Wallet y se publiquen las dos a la vez.
  walletEnabled: boolean = AppSettings.WALLET;
  // Cuál de los dos pases se está generando, para que el spinner salga en su
  // botón y no en los dos.
  walletLoading: 'google' | 'apple' | null = null;
  walletError: string | null = null;

  /**
   * Se ofrece una sola insignia, la del wallet que le sirve al dispositivo: la
   * de Apple solo en iPhone y iPad, y la de Google en todo lo demás.
   *
   * El Mac queda fuera a propósito, aunque Safari sepa guardar el `.pkpass` en
   * el Wallet de iCloud: en un escritorio no se sabe qué teléfono lleva quien
   * mira, y el enlace de Google vale para cualquiera. En Android y en
   * Windows/Linux tampoco se pinta, que allí la descarga solo daría un fichero
   * inservible y las normas de marca de Apple piden no ofrecer el pase donde no
   * se puede guardar.
   *
   * El iPad hay que reconocerlo por el táctil: desde iPadOS 13 Safari se anuncia
   * como `Macintosh` y por el user agent no hay forma de distinguirlo de un Mac.
   */
  get appleWalletAvailable(): boolean {
    const userAgent = navigator.userAgent || '';
    const isIpadOs = /Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;

    return /iPad|iPhone|iPod/.test(userAgent) || isIpadOs;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private seasonPassService: SeasonPassService,
  ) { }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.router.navigate(['/profile/abonos']);
      return;
    }
    this.loadSeasonPass(id);
  }

  /**
   * No hay endpoint de "mi abono por id", así que se reusa el listado de
   * "mis abonos" y se busca el que toca. Si no existe o no está activo, no
   * hay carnet válido que enseñar y se vuelve al listado.
   */
  private loadSeasonPass(id: number): void {
    this.seasonPassService.getMySeasonPasses().subscribe({
      next: (res) => {
        const pass: SeasonPass | undefined = (res.data || []).find((sp: SeasonPass) => sp.id === id);

        if (!pass || pass.status?.slug !== 'active' || !pass.hash) {
          this.router.navigate(['/profile/abonos']);
          return;
        }

        this.seasonPass = pass;
        this.loaded = true;
      },
      error: () => {
        this.loadError = true;
        this.loaded = true;
      },
    });
  }

  get title(): string {
    return this.seasonPass?.season?.name || 'Abono de temporada';
  }

  get companyName(): string | null {
    return this.seasonPass?.season?.company?.name || null;
  }

  get statusName(): string {
    return this.seasonPass?.status?.name || 'Activo';
  }

  get seatDisplay(): string | null {
    return seasonPassSeatLabel(this.seasonPass);
  }

  /**
   * Pide al backend el enlace "Añadir a Google Wallet" y lo abre. El pase se
   * genera en el servidor (JWT firmado); aquí solo se redirige a la URL.
   */
  addToGoogleWallet(): void {
    if (!this.seasonPass || this.walletLoading) return;

    this.walletLoading = 'google';
    this.walletError = null;

    this.seasonPassService.getGoogleWalletUrl(this.seasonPass.id).subscribe({
      next: (res) => {
        this.walletLoading = null;
        const saveUrl = res?.data?.save_url;
        if (saveUrl) {
          window.open(saveUrl, '_blank');
        } else {
          this.walletError = 'No se ha podido generar el pase. Inténtalo de nuevo más tarde.';
        }
      },
      error: (err) => {
        this.walletLoading = null;
        this.walletError = err?.error?.message || 'No se ha podido generar el pase. Inténtalo de nuevo más tarde.';
      },
    });
  }

  /**
   * Pide al backend la URL firmada del `.pkpass` y **navega** a ella, no la abre
   * en otra pestaña: iOS solo ofrece "Añadir a Wallet" cuando el fichero llega
   * por una navegación normal. La URL caduca a los 5 minutos, así que se pide
   * en cada clic en vez de guardarla.
   */
  addToAppleWallet(): void {
    if (!this.seasonPass || this.walletLoading) return;

    this.walletLoading = 'apple';
    this.walletError = null;

    this.seasonPassService.getAppleWalletUrl(this.seasonPass.id).subscribe({
      next: (res) => {
        this.walletLoading = null;
        const downloadUrl = res?.data?.download_url;
        if (downloadUrl) {
          window.location.href = downloadUrl;
        } else {
          this.walletError = 'No se ha podido generar el pase. Inténtalo de nuevo más tarde.';
        }
      },
      error: (err) => {
        this.walletLoading = null;
        this.walletError = err?.error?.message || 'No se ha podido generar el pase. Inténtalo de nuevo más tarde.';
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/profile/abonos']);
  }
}
